<template>
  <v-card :loading="isLoading" flat class="payment-monitoring-page">
    <v-card-title class="payment-monitoring-page-header align-start flex-wrap">
      <div>
        <h1 class="payment-monitoring-page-title">
          TYA Tahsilat ve Hakediş Özeti
        </h1>
        <div class="payment-monitoring-page-subtitle">
          Onaylanan hakedişleri muhasebeleşen Iyzico transferleriyle
          karşılaştırın.
        </div>
      </div>
      <v-spacer />
      <v-btn small outlined color="primary" @click="loadCollections">
        <v-icon small left>mdi-refresh</v-icon>
        Yenile
      </v-btn>
    </v-card-title>

    <v-card-text class="pa-0">
      <div class="payment-monitoring-context-row">
        <span class="payment-monitoring-context-chip">
          Seçilen tarih aralığı: <strong>{{ periodLabel }}</strong>
        </span>
        <span class="payment-monitoring-context-chip">
          Durum tarihi: <strong>{{ effectiveAsOfLabel }}</strong>
        </span>
        <span class="payment-monitoring-context-chip">
          Kapsam: <strong>Iyzico banka transferleri</strong>
        </span>
      </div>

      <div class="payment-monitoring-callout collection-monitoring-callout">
        <v-icon small color="info">mdi-information-outline</v-icon>
        <span>
          Aktarılacak tutar dönem başı devreden ile seçilen dönemde onaylanan
          hakedişlerin toplamıdır. Aktarılan tutara yalnız eşleşmiş ve doğru iç
          transferle muhasebeleşmiş Iyzico banka hareketleri girer.
        </span>
      </div>

      <payment-monitoring-filters
        v-model="filters"
        :status-items="statusItems"
        query-label="TYA adı veya ID"
        @apply="applyFilters"
      />

      <v-row dense class="collection-metrics-row">
        <v-col
          v-for="metric in metrics"
          :key="metric.label"
          cols="12"
          sm="6"
          md="3"
        >
          <payment-monitoring-metric v-bind="metric" />
        </v-col>
      </v-row>

      <v-card outlined class="payment-monitoring-panel">
        <v-card-title class="payment-monitoring-panel-header">
          <div>
            <div class="payment-monitoring-section-title">
              TYA bazında hakediş durumu
            </div>
            <div class="payment-monitoring-section-description">
              Önce işlem gereken kayıtlar, ardından bekleyen ve tamamlananlar
              gösterilir.
            </div>
          </div>
        </v-card-title>

        <v-data-table
          :headers="headers"
          :items="response.items || []"
          :loading="isLoading"
          :server-items-length="response.pagination.total || 0"
          :options.sync="options"
          :footer-props="{ 'items-per-page-options': [10, 20, 50] }"
          no-data-text="Seçilen filtrelerde hakediş kaydı bulunamadı."
        >
          <template v-slot:item.cluster_name="{ item }">
            <strong class="collection-cluster-name">
              {{ item.cluster_name }}
            </strong>
            <div class="caption text--secondary">
              {{ item.payment_provider_name }} · TYA #{{ item.cluster_id }}
            </div>
          </template>

          <template v-slot:item.transferable_amount="{ value }">
            <strong>
              <rs-table-cell-number price :value="value" />
            </strong>
          </template>

          <template v-slot:item.transferred_amount="{ value }">
            <span class="collection-transferred-amount">
              <rs-table-cell-number price :value="value" />
            </span>
          </template>

          <template v-slot:item.waiting_amount="{ value }">
            <strong :class="{ 'collection-waiting-amount': value > 0 }">
              <rs-table-cell-number price :value="value" />
            </strong>
          </template>

          <template v-slot:item.status_priority="{ item }">
            <v-chip
              x-small
              outlined
              :color="statusMeta(item.status.code).color"
            >
              <v-icon x-small left>
                {{ statusMeta(item.status.code).icon }}
              </v-icon>
              {{ item.status.label }}
            </v-chip>
          </template>

          <template v-slot:item.actions="{ item }">
            <v-btn
              icon
              small
              aria-label="Hakediş detayını aç"
              @click="openDetail(item)"
            >
              <v-icon small>mdi-chevron-right</v-icon>
            </v-btn>
          </template>
        </v-data-table>
      </v-card>
    </v-card-text>

    <v-navigation-drawer
      v-model="detailOpen"
      temporary
      fixed
      right
      width="640"
      class="payment-monitoring-drawer"
    >
      <v-card v-if="selectedItem" flat class="collection-detail-card">
        <v-card-title class="collection-detail-header">
          <div class="collection-detail-header-copy">
            <div class="collection-detail-title">
              {{ selectedItem.cluster_name }}
            </div>
            <div class="collection-detail-header-meta">
              <span>
                {{ selectedItem.payment_provider_name }} · TYA #{{
                  selectedItem.cluster_id
                }}
                · {{ periodLabel }}
              </span>
              <v-chip
                x-small
                outlined
                :color="statusMeta(selectedItem.status.code).color"
              >
                {{ selectedItem.status.label }}
              </v-chip>
            </div>
          </div>
          <v-spacer />
          <v-btn icon aria-label="Detayı kapat" @click="detailOpen = false">
            <v-icon>mdi-close</v-icon>
          </v-btn>
        </v-card-title>

        <v-divider />

        <v-card-text class="collection-detail-body">
          <v-skeleton-loader
            v-if="isDetailLoading"
            type="article, table, table"
          />

          <v-alert v-else-if="detailError" outlined type="error">
            Hakediş detayı alınamadı. Listeyi yenileyip tekrar deneyin.
          </v-alert>

          <template v-else-if="detailItem">
            <div
              class="collection-detail-action"
              :class="
                'collection-detail-action--' +
                recommendedActionType(detailItem.status.code)
              "
            >
              <v-icon
                :color="statusMeta(detailItem.status.code).color"
                size="20"
              >
                {{ statusMeta(detailItem.status.code).icon }}
              </v-icon>
              <div>
                <strong>{{ detailItem.status.label }}</strong>
                <span>{{ detailStatusDescription }}</span>
              </div>
            </div>

            <div class="collection-detail-section">
              <div class="collection-detail-section-title">Hakediş durumu</div>
              <div class="collection-primary-summary">
                <div class="collection-primary-metric">
                  <span>TYA'ya aktarılacak</span>
                  <strong>
                    <rs-table-cell-number
                      price
                      :value="detailItem.transferable_amount"
                    />
                  </strong>
                </div>
                <div
                  class="collection-primary-metric collection-primary-metric--success"
                >
                  <span>Aktarılan</span>
                  <strong>
                    <rs-table-cell-number
                      price
                      :value="detailItem.transferred_amount"
                    />
                  </strong>
                </div>
                <div
                  class="collection-primary-metric"
                  :class="{
                    'collection-primary-metric--warning':
                      detailItem.waiting_amount > 0,
                  }"
                >
                  <span>Bekleyen</span>
                  <strong>
                    <rs-table-cell-number
                      price
                      :value="detailItem.waiting_amount"
                    />
                  </strong>
                </div>
              </div>

              <v-expansion-panels
                accordion
                flat
                class="collection-inline-expansion collection-calculation-expansion"
              >
                <v-expansion-panel>
                  <v-expansion-panel-header>
                    Tutar nasıl hesaplandı?
                  </v-expansion-panel-header>
                  <v-expansion-panel-content>
                    <div class="collection-calculation-formula">
                      <div>
                        <span>Önceki dönemden bekleyen</span>
                        <strong>
                          <rs-table-cell-number
                            price
                            :value="detailItem.opening_waiting_amount"
                          />
                        </strong>
                      </div>
                      <v-icon small>mdi-plus</v-icon>
                      <div>
                        <span>Bu dönemde onaylanan</span>
                        <strong>
                          <rs-table-cell-number
                            price
                            :value="detailItem.period_approved_amount"
                          />
                        </strong>
                      </div>
                    </div>
                    <div
                      v-if="detailItem.excess_amount > 0"
                      class="collection-excess-note"
                    >
                      Fazla aktarılan:
                      <strong class="error--text">
                        <rs-table-cell-number
                          price
                          :value="detailItem.excess_amount"
                        />
                      </strong>
                    </div>
                  </v-expansion-panel-content>
                </v-expansion-panel>
              </v-expansion-panels>
            </div>

            <div
              v-if="detailItem.waiting_amount > 0"
              class="collection-detail-section"
            >
              <div class="collection-detail-section-title">Bekleyen tutar</div>
              <div class="collection-waiting-overview">
                <v-icon small :color="waitingOverviewTone" class="mt-1">
                  {{ waitingOverviewIcon }}
                </v-icon>
                <span>{{ waitingOverviewText }}</span>
              </div>

              <div class="collection-breakdown-list">
                <div
                  v-for="item in waitingBreakdown"
                  :key="item.code"
                  class="collection-breakdown-row"
                  :class="'collection-breakdown-row--' + item.code"
                >
                  <span>{{ item.label }}</span>
                  <strong>
                    <rs-table-cell-number price :value="item.value" />
                  </strong>
                </div>
              </div>

              <v-expansion-panels
                v-if="detailItem.daily_schedule.length"
                accordion
                flat
                class="collection-inline-expansion collection-schedule-expansion"
              >
                <v-expansion-panel>
                  <v-expansion-panel-header>
                    <div class="collection-expansion-title">
                      <span>Günlük plan</span>
                      <small>
                        {{ detailItem.daily_schedule.length }} aktarım günü
                      </small>
                    </div>
                  </v-expansion-panel-header>
                  <v-expansion-panel-content>
                    <div class="collection-schedule-list">
                      <div
                        v-for="schedule in detailItem.daily_schedule"
                        :key="schedule.expected_on"
                        class="collection-schedule-row"
                      >
                        <strong>{{
                          formatDateOnly(schedule.expected_on)
                        }}</strong>
                        <span>{{ schedule.payment_count }} ödeme</span>
                        <strong>
                          <rs-table-cell-number
                            price
                            :value="schedule.amount"
                          />
                        </strong>
                      </div>
                    </div>
                  </v-expansion-panel-content>
                </v-expansion-panel>
              </v-expansion-panels>
            </div>

            <div class="collection-detail-section">
              <div class="collection-detail-section-title">
                Gerçekleşen transferler
              </div>

              <div
                v-if="!detailItem.bank_movements.length"
                class="collection-empty-note"
              >
                <v-icon small color="grey lighten-1">
                  mdi-bank-transfer-out
                </v-icon>
                <span>Bu dönemde gerçekleşen transfer bulunmuyor.</span>
              </div>

              <v-expansion-panels
                v-else
                accordion
                flat
                class="collection-detail-expansions"
              >
                <v-expansion-panel
                  v-for="movement in detailItem.bank_movements"
                  :key="movement.bank_activity_id"
                >
                  <v-expansion-panel-header>
                    <div class="collection-movement-header">
                      <div>
                        <strong>{{ formatDate(movement.activity_on) }}</strong>
                        <div
                          class="collection-movement-status"
                          :class="
                            'collection-movement-status--' +
                            movement.accounting_status
                          "
                        >
                          {{
                            accountingStatusMeta(movement.accounting_status)
                              .text
                          }}
                        </div>
                      </div>
                      <strong>
                        <rs-table-cell-number price :value="movement.amount" />
                      </strong>
                    </div>
                  </v-expansion-panel-header>
                  <v-expansion-panel-content>
                    <p class="collection-movement-description">
                      {{ movement.description || "Açıklama yok" }}
                    </p>
                    <div class="collection-movement-details">
                      <div>
                        <span>Muhasebeleşen tutar</span>
                        <strong>
                          <rs-table-cell-number
                            price
                            :value="movement.accounted_amount"
                          />
                        </strong>
                      </div>
                      <div>
                        <span>Banka hareketi</span>
                        <strong>#{{ movement.bank_activity_id }}</strong>
                      </div>
                      <div>
                        <span>İç transfer</span>
                        <strong>{{
                          transferIdsText(movement.transfer_ids)
                        }}</strong>
                      </div>
                      <div>
                        <span>Alıcı hesap</span>
                        <strong
                          >#{{ movement.receiving_bank_account_id }}</strong
                        >
                      </div>
                    </div>
                    <v-alert
                      v-if="movement.configured_account_mismatch"
                      dense
                      text
                      type="warning"
                      class="mt-2 mb-0"
                    >
                      Hareket, provider kaydındaki hesaptan farklı fakat aynı
                      TYA'ya ait gerçek alıcı hesaba gelmiş.
                    </v-alert>
                  </v-expansion-panel-content>
                </v-expansion-panel>
              </v-expansion-panels>
            </div>

            <div
              class="collection-bank-data"
              :class="{
                'collection-bank-data--stale': detailItem.bank_data.is_stale,
              }"
            >
              <v-icon
                v-if="detailItem.bank_data.is_stale"
                small
                color="warning"
              >
                mdi-bank-sync
              </v-icon>
              <span>
                Son banka güncellemesi:
                {{ formatDate(detailItem.bank_data.last_fetched_at) }}
              </span>
              <v-chip
                v-if="detailItem.bank_data.is_stale"
                x-small
                outlined
                color="warning"
              >
                Güncel değil
              </v-chip>
            </div>
          </template>
        </v-card-text>

        <v-card-actions class="collection-detail-footer">
          <span class="collection-detail-footer-note">
            {{ effectiveAsOfLabel }} itibarıyla güncel durum
          </span>
          <v-spacer />
          <v-btn text @click="detailOpen = false">Kapat</v-btn>
        </v-card-actions>
      </v-card>
    </v-navigation-drawer>
  </v-card>
</template>

<script>
import { hasPermissions, isPage } from "@/view/mixins";
import PaymentMonitoringFilters from "./components/PaymentMonitoringFilters.vue";
import PaymentMonitoringMetric from "./components/PaymentMonitoringMetric.vue";
import {
  buildPaymentMonitoringParams,
  formatMonitoringDate,
  paymentMonitoringFiltersFromQuery,
  paymentMonitoringOptionsFromQuery,
  paymentMonitoringStatusesFromQuery,
  syncPaymentMonitoringUrl,
} from "./paymentMonitoring";

const emptyResponse = () => ({
  meta: { period: {} },
  summary: {},
  items: [],
  pagination: { total: 0 },
});

const collectionStatuses = [
  { id: "accounting_required", text: "Muhasebeleştirme gerekli" },
  { id: "review_required", text: "Kontrol gerekli" },
  { id: "bank_data_unavailable", text: "Banka verisi yok" },
  { id: "waiting", text: "Bekliyor" },
  { id: "completed", text: "Tamamlandı" },
];

const statusDefinitions = {
  accounting_required: {
    color: "warning",
    icon: "mdi-bank-plus",
  },
  review_required: {
    color: "error",
    icon: "mdi-alert-circle-outline",
  },
  bank_data_unavailable: {
    color: "grey darken-1",
    icon: "mdi-bank-off-outline",
  },
  waiting: {
    color: "primary",
    icon: "mdi-clock-outline",
  },
  completed: {
    color: "success",
    icon: "mdi-check-circle-outline",
  },
};

export default {
  name: "TyaCollectionsHakedis",
  components: { PaymentMonitoringFilters, PaymentMonitoringMetric },
  mixins: [hasPermissions, isPage],
  data() {
    const filters = paymentMonitoringFiltersFromQuery(this.$route.query);
    const allowedStatuses = collectionStatuses.map((item) => item.id);
    filters.statuses = paymentMonitoringStatusesFromQuery(
      this.$route.query
    ).filter((status) => allowedStatuses.includes(status));

    return {
      pageMeta: { title: "TYA Tahsilat ve Hakediş Özeti" },
      isLoading: false,
      isDetailLoading: false,
      detailError: false,
      detailOpen: false,
      selectedItem: null,
      detailItem: null,
      filters,
      statusItems: collectionStatuses,
      response: emptyResponse(),
      options: paymentMonitoringOptionsFromQuery(this.$route.query, {
        page: 1,
        itemsPerPage: 20,
        sortBy: ["status_priority"],
        sortDesc: [false],
      }),
      headers: [
        { text: "TYA", value: "cluster_name" },
        {
          text: "TYA'ya aktarılacak",
          value: "transferable_amount",
          align: "end",
        },
        { text: "Aktarılan", value: "transferred_amount", align: "end" },
        { text: "Bekleyen", value: "waiting_amount", align: "end" },
        { text: "Durum", value: "status_priority" },
        { text: "", value: "actions", sortable: false, align: "end" },
      ],
    };
  },
  computed: {
    metrics() {
      const value = this.response.summary || {};
      return [
        {
          label: "TYA'ya aktarılacak",
          value: value.transferable_amount,
          money: true,
          caption: "Devreden + dönem içinde onaylanan",
          icon: "mdi-office-building-outline",
          color: "primary",
          footLabel: "TYA",
          footValue: value.cluster_count || 0,
        },
        {
          label: "Aktarılan",
          value: value.transferred_amount,
          money: true,
          caption: "Doğru muhasebeleşmiş transfer",
          icon: "mdi-bank-check",
          color: "success",
          footLabel: "Kanıt",
          footValue: "Banka + iç transfer",
        },
        {
          label: "Bekleyen",
          value: value.waiting_amount,
          money: true,
          caption: "Aktarılacak - aktarılan",
          icon: "mdi-clock-outline",
          color: "warning",
          footLabel: "Durum tarihi",
          footValue: this.effectiveAsOfLabel,
        },
        {
          label: "İncelenecek TYA",
          value: value.attention_count,
          caption: "İşlem veya kontrol gereken",
          icon: "mdi-alert-decagram-outline",
          color: "info",
          footLabel: "Toplam TYA",
          footValue: value.cluster_count || 0,
        },
      ];
    },
    periodLabel() {
      const period = this.response.meta?.period || {};
      return period.min && period.max
        ? this.formatDateOnly(period.min) +
            " - " +
            this.formatDateOnly(period.max)
        : "-";
    },
    effectiveAsOfLabel() {
      return this.formatDateOnly(
        this.response.meta?.period?.effective_as_of ||
          this.filters.dateRange.max
      );
    },
    detailStatusDescription() {
      if (
        this.detailItem?.status?.code === "waiting" &&
        Number(this.detailItem?.waiting_breakdown?.overdue_amount || 0) <= 0
      ) {
        return "Geciken aktarım yok; planlanan tarihler bekleniyor.";
      }

      return this.detailItem?.recommended_action?.label || "-";
    },
    waitingOverviewText() {
      const value = this.detailItem?.waiting_breakdown || {};
      const overdue = Number(value.overdue_amount || 0);
      const dueToday = Number(value.due_today_amount || 0);
      const future = Number(value.future_amount || 0);
      const unavailable = Number(value.schedule_unavailable_amount || 0);

      if (overdue > 0) return "Gecikmiş aktarım bulunuyor; kontrol edilmeli.";
      if (unavailable > 0)
        return "Bir bölümünün beklenen aktarım tarihi bulunamıyor.";
      if (dueToday > 0) return "Bugün aktarılması beklenen tutar bulunuyor.";
      if (future > 0)
        return "Bekleyen tutarın tamamı ileri tarihli. Geciken aktarım yok.";

      return "Bekleyen tutarın aktarım planı bulunamadı.";
    },
    waitingOverviewTone() {
      const value = this.detailItem?.waiting_breakdown || {};
      if (Number(value.overdue_amount || 0) > 0) return "error";
      if (Number(value.schedule_unavailable_amount || 0) > 0) return "warning";
      return "primary";
    },
    waitingOverviewIcon() {
      const value = this.detailItem?.waiting_breakdown || {};
      if (Number(value.overdue_amount || 0) > 0)
        return "mdi-alert-circle-outline";
      if (Number(value.schedule_unavailable_amount || 0) > 0)
        return "mdi-calendar-question-outline";
      return "mdi-calendar-clock-outline";
    },
    waitingBreakdown() {
      const value = this.detailItem?.waiting_breakdown || {};
      return [
        {
          code: "overdue",
          label: "Gecikmiş",
          value: value.overdue_amount,
        },
        {
          code: "due-today",
          label: "Bugün beklenen",
          value: value.due_today_amount,
        },
        {
          code: "future",
          label: "İleri tarihli",
          value: value.future_amount,
        },
        {
          code: "unavailable",
          label: "Plan tarihi bulunamayan",
          value: value.schedule_unavailable_amount,
        },
      ].filter((item) => Number(item.value || 0) > 0);
    },
  },
  watch: {
    options: {
      deep: true,
      handler() {
        this.syncUrlState();
        this.loadCollections();
      },
    },
  },
  mounted() {
    this.syncUrlState();
    this.loadCollections();
  },
  methods: {
    applyFilters() {
      if (this.options.page !== 1) this.options.page = 1;
      else {
        this.syncUrlState();
        this.loadCollections();
      }
    },
    syncUrlState() {
      syncPaymentMonitoringUrl(this.filters, this.options);
    },
    loadCollections() {
      if (this.isLoading) return;
      this.isLoading = true;
      const params = buildPaymentMonitoringParams(this.filters, {
        page: this.options.page,
        per_page: this.options.itemsPerPage,
        order_by: this.options.sortBy[0] || "status_priority",
        order: this.options.sortDesc[0] ? "desc" : "asc",
      });

      this.$api
        .query("api-super-admin/payment-monitoring/collections", { params })
        .then(
          (response) => (this.response = response.data.data || emptyResponse())
        )
        .catch((error) => this.handleError(error))
        .finally(() => (this.isLoading = false));
    },
    openDetail(item) {
      this.selectedItem = item;
      this.detailItem = null;
      this.detailError = false;
      this.detailOpen = true;
      this.loadDetail(item.cluster_payment_provider_id);
    },
    loadDetail(clusterPaymentProviderId) {
      this.isDetailLoading = true;
      const params = buildPaymentMonitoringParams(this.filters);

      this.$api
        .query(
          "api-super-admin/payment-monitoring/collections/" +
            clusterPaymentProviderId,
          { params }
        )
        .then((response) => {
          if (
            this.selectedItem?.cluster_payment_provider_id ===
            clusterPaymentProviderId
          ) {
            this.detailItem = response.data.data?.item || null;
          }
        })
        .catch((error) => {
          this.detailError = true;
          this.handleError(error);
        })
        .finally(() => (this.isDetailLoading = false));
    },
    statusMeta(status) {
      return (
        statusDefinitions[status] || {
          color: "grey",
          icon: "mdi-help-circle-outline",
        }
      );
    },
    recommendedActionType(status) {
      if (status === "completed") return "success";
      if (status === "waiting") return "info";
      return "warning";
    },
    accountingStatusMeta(status) {
      return (
        {
          accounted: { text: "Muhasebeleşti", color: "success" },
          partial: { text: "Kısmi muhasebeleşti", color: "warning" },
          missing: { text: "Muhasebeleştirilmedi", color: "error" },
        }[status] || { text: status || "-", color: "grey" }
      );
    },
    transferIdsText(ids) {
      return ids?.length ? ids.map((id) => "#" + id).join(", ") : "-";
    },
    formatDateOnly(value) {
      if (!value) return "-";
      const [year, month, day] = String(value).slice(0, 10).split("-");
      return year && month && day ? day + "." + month + "." + year : value;
    },
    formatDate: formatMonitoringDate,
  },
};
</script>

<style lang="scss">
@import "./payment-monitoring.scss";
</style>

<style scoped>
.collection-monitoring-callout {
  margin-top: 0;
  margin-bottom: 12px;
}

.collection-metrics-row {
  margin-bottom: 18px;
}

.collection-cluster-name {
  color: #181c32;
}

.collection-transferred-amount {
  color: #0f8f88;
}

.collection-waiting-amount {
  color: #b77900;
}

.collection-detail-card {
  display: flex;
  min-height: 100%;
  height: 100%;
  flex-direction: column;
  overflow: hidden;
}

.collection-detail-header {
  flex: 0 0 auto;
  gap: 14px;
  padding: 18px 20px !important;
  background: #ffffff;
}

.collection-detail-header-copy {
  min-width: 0;
}

.collection-detail-title {
  color: #181c32;
  font-size: 19px;
  font-weight: 800;
  line-height: 1.25;
}

.collection-detail-header-meta {
  display: flex;
  flex-wrap: wrap;
  align-items: center;
  gap: 8px;
  margin-top: 6px;
  color: #7e8299;
  font-size: 12px;
  font-weight: 600;
}

.collection-detail-body {
  flex: 1 1 auto;
  overflow-y: auto;
  padding: 24px !important;
}

.collection-detail-action {
  display: flex;
  align-items: flex-start;
  gap: 12px;
  margin-bottom: 28px;
  padding: 14px 16px;
  border: 1px solid #dce5ff;
  border-radius: 6px;
  background: #f7f9ff;
}

.collection-detail-action--success {
  border-color: rgba(27, 197, 189, 0.28);
  background: rgba(27, 197, 189, 0.07);
}

.collection-detail-action--warning {
  border-color: rgba(255, 168, 0, 0.3);
  background: rgba(255, 168, 0, 0.08);
}

.collection-detail-action strong,
.collection-detail-action span {
  display: block;
}

.collection-detail-action strong {
  color: #3f4254;
  font-size: 13px;
  font-weight: 800;
}

.collection-detail-action span {
  margin-top: 3px;
  color: #7e8299;
  font-size: 12px;
  line-height: 1.5;
}

.collection-detail-section {
  margin-bottom: 30px;
}

.collection-detail-section-title {
  margin-bottom: 12px;
  color: #5e6278;
  font-size: 12px;
  font-weight: 800;
  letter-spacing: 0.025em;
  line-height: 1.4;
  text-transform: uppercase;
}

.collection-primary-summary {
  display: grid;
  grid-template-columns: repeat(3, minmax(0, 1fr));
  gap: 10px;
}

.collection-primary-metric {
  display: flex;
  min-width: 0;
  min-height: 94px;
  justify-content: space-between;
  padding: 15px;
  border: 1px solid #dfe3f3;
  border-top: 3px solid #5867dd;
  border-radius: 6px;
  background: #fafbff;
  flex-direction: column;
}

.collection-primary-metric--success {
  border-color: rgba(27, 197, 189, 0.28);
  border-top-color: #1bc5bd;
  background: rgba(27, 197, 189, 0.06);
}

.collection-primary-metric--warning {
  border-color: rgba(255, 168, 0, 0.3);
  border-top-color: #ffa800;
  background: rgba(255, 168, 0, 0.07);
}

.collection-primary-metric > span {
  color: #7e8299;
  font-size: 11px;
  font-weight: 700;
  line-height: 1.35;
}

.collection-primary-metric strong {
  overflow: hidden;
  color: #181c32;
  font-size: 17px;
  font-weight: 800;
  letter-spacing: -0.02em;
  line-height: 1.25;
  text-overflow: ellipsis;
  white-space: nowrap;
}

.collection-primary-metric strong ::v-deep span {
  font-size: 17px !important;
  font-weight: 800 !important;
}

.collection-inline-expansion {
  overflow: hidden;
  margin-top: 10px;
  border: 1px solid #eff1f5;
  border-radius: 4px;
}

.collection-inline-expansion ::v-deep .v-expansion-panel::before {
  box-shadow: none;
}

.collection-inline-expansion ::v-deep .v-expansion-panel-header {
  min-height: 44px;
  padding: 0 14px;
  color: #7e8299;
  font-size: 11px;
  font-weight: 700;
}

.collection-inline-expansion ::v-deep .v-expansion-panel-content__wrap {
  padding: 0 14px 14px;
}

.collection-calculation-formula {
  display: flex;
  align-items: center;
  gap: 10px;
}

.collection-calculation-formula > div {
  display: flex;
  min-width: 0;
  flex: 1 1 0;
  align-items: center;
  justify-content: space-between;
  gap: 10px;
  padding: 9px 10px;
  border-radius: 4px;
  background: #f7f8fb;
}

.collection-calculation-formula span {
  color: #7e8299;
  font-size: 11px;
  font-weight: 600;
}

.collection-calculation-formula strong {
  color: #3f4254;
  font-size: 12px;
  font-weight: 800;
  white-space: nowrap;
}

.collection-calculation-formula strong ::v-deep span {
  font-size: 12px !important;
  font-weight: 800 !important;
}

.collection-excess-note {
  margin-top: 8px;
  color: #7e8299;
  font-size: 11px;
  text-align: right;
}

.collection-waiting-overview {
  display: flex;
  align-items: flex-start;
  gap: 8px;
  color: #5e6278;
  font-size: 12px;
  font-weight: 600;
  line-height: 1.5;
}

.collection-breakdown-list {
  overflow: hidden;
  margin-top: 10px;
  border: 1px solid #eff1f5;
  border-radius: 4px;
}

.collection-breakdown-row {
  display: flex;
  min-height: 42px;
  align-items: center;
  justify-content: space-between;
  gap: 12px;
  padding: 8px 12px;
  border-bottom: 1px solid #eff1f5;
  color: #7e8299;
  font-size: 11px;
  font-weight: 700;
}

.collection-breakdown-row:last-child {
  border-bottom: 0;
}

.collection-breakdown-row--overdue {
  background: rgba(246, 78, 96, 0.05);
  color: #f64e60;
}

.collection-breakdown-row--due-today,
.collection-breakdown-row--unavailable {
  background: rgba(255, 168, 0, 0.05);
}

.collection-breakdown-row strong {
  color: #181c32;
  font-size: 12px;
  font-weight: 800;
}

.collection-schedule-expansion {
  margin-top: 8px;
}

.collection-expansion-title {
  display: flex;
  width: 100%;
  align-items: center;
  justify-content: space-between;
  gap: 12px;
  padding-right: 10px;
}

.collection-expansion-title small {
  color: #a1a5b7;
  font-size: 10px;
  font-weight: 600;
}

.collection-schedule-list {
  border-top: 1px solid #eff1f5;
}

.collection-schedule-row {
  display: grid;
  min-height: 42px;
  align-items: center;
  padding: 8px 2px;
  border-bottom: 1px solid #eff1f5;
  color: #3f4254;
  font-size: 11px;
  grid-template-columns: minmax(90px, 1fr) minmax(70px, 0.7fr) minmax(
      110px,
      1fr
    );
}

.collection-schedule-row:last-child {
  border-bottom: 0;
}

.collection-schedule-row > span {
  color: #a1a5b7;
}

.collection-schedule-row > :last-child {
  text-align: right;
}

.collection-detail-expansions {
  overflow: hidden;
  border: 1px solid #e4e6ef;
  border-radius: 4px;
}

.collection-detail-expansions ::v-deep .v-expansion-panel::before {
  box-shadow: none;
}

.collection-detail-expansions ::v-deep .v-expansion-panel-header {
  min-height: 56px;
  color: #3f4254;
  font-size: 13px;
  font-weight: 700;
}

.collection-empty-note {
  display: flex;
  align-items: center;
  gap: 7px;
  padding: 10px 12px;
  border: 1px dashed #e4e6ef;
  border-radius: 4px;
  color: #a1a5b7;
  font-size: 10px;
  font-weight: 600;
}

.collection-movement-header {
  display: flex;
  width: 100%;
  align-items: center;
  justify-content: space-between;
  gap: 14px;
  padding-right: 12px;
}

.collection-movement-status {
  margin-top: 3px;
  color: #7e8299;
  font-size: 10px;
  font-weight: 600;
}

.collection-movement-status--accounted {
  color: #0f9f96;
}

.collection-movement-status--partial {
  color: #c78200;
}

.collection-movement-status--missing {
  color: #d83b4b;
}

.collection-movement-description {
  padding: 10px 12px;
  border-radius: 4px;
  background: #f5f7fa;
  color: #5e6278;
  font-size: 11px;
  line-height: 1.5;
  word-break: break-word;
}

.collection-movement-details {
  display: grid;
  overflow: hidden;
  border: 1px solid #eff1f5;
  border-radius: 4px;
  grid-template-columns: repeat(2, minmax(0, 1fr));
}

.collection-movement-details > div {
  display: flex;
  min-height: 48px;
  align-items: center;
  justify-content: space-between;
  gap: 10px;
  padding: 9px 11px;
  border-right: 1px solid #eff1f5;
  border-bottom: 1px solid #eff1f5;
}

.collection-movement-details > div:nth-child(2n) {
  border-right: 0;
}

.collection-movement-details > div:nth-last-child(-n + 2) {
  border-bottom: 0;
}

.collection-movement-details span {
  color: #a1a5b7;
  font-size: 10px;
  font-weight: 600;
}

.collection-movement-details strong {
  color: #3f4254;
  font-size: 11px;
  font-weight: 800;
  text-align: right;
}

.collection-bank-data {
  display: flex;
  flex-wrap: wrap;
  align-items: center;
  gap: 6px;
  color: #b5b5c3;
  font-size: 9px;
  font-weight: 500;
}

.collection-bank-data--stale {
  padding: 8px 10px;
  border: 1px solid rgba(255, 168, 0, 0.25);
  border-radius: 4px;
  background: rgba(255, 168, 0, 0.05);
  color: #9c6b00;
  font-size: 10px;
  font-weight: 600;
}

.collection-detail-footer {
  flex: 0 0 auto;
  gap: 8px;
  padding: 12px 16px !important;
  border-top: 1px solid #eff1f5;
  background: #ffffff;
}

.collection-detail-footer-note {
  color: #7e8299;
  font-size: 10px;
  font-weight: 600;
  line-height: 1.35;
}

@media (max-width: 959px) {
  .collection-detail-footer-note {
    display: none;
  }
}

@media (max-width: 599px) {
  .collection-primary-summary,
  .collection-movement-details {
    grid-template-columns: 1fr;
  }

  .collection-calculation-formula {
    align-items: stretch;
    flex-direction: column;
  }

  .collection-calculation-formula > .v-icon {
    transform: rotate(90deg);
  }

  .collection-movement-details > div,
  .collection-movement-details > div:nth-child(2n),
  .collection-movement-details > div:nth-last-child(-n + 2) {
    border-right: 0;
    border-bottom: 1px solid #eff1f5;
  }

  .collection-movement-details > div:last-child {
    border-bottom: 0;
  }
}
</style>
